import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Search, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { download } from "@/lib/download";
import { formatNumber } from "@/lib/format";
import LabelDialog from "@/components/labels/LabelDialog";

const key = (l) => `${l.po_id}:${l.line_id}`;

function Line({ l, on, toggle }) {
  return (
    <tr className={l.eligible ? "" : "text-slate-400 bg-slate-50/60"} data-testid={`impbc-line-${l.po_id}-${l.line_id}`}>
      <td><input type="checkbox" disabled={!l.eligible} checked={!!on} onChange={() => toggle(l)} data-testid={`impbc-pick-${l.po_id}-${l.line_id}`} /></td>
      <td className="font-mono text-xs">{l.sku || "—"}</td><td className="text-xs">{l.name}</td>
      <td className="text-[11px]">{l.state_label}</td>
      <td className="text-right font-mono text-xs">{formatNumber(l.qty)}{l.carton_count ? ` · ${formatNumber(l.carton_count)} koli` : ""}</td>
      <td className="font-mono text-xs" data-testid={`impbc-status-${l.po_id}-${l.line_id}`}>{l.barcode || (l.reason ? <span className="font-sans text-amber-700">{l.reason}</span> : <span className="font-sans text-emerald-700">Barkod üretilebilir</span>)}</td>
    </tr>
  );
}

function Result({ done }) {
  const [lbl, setLbl] = useState(false);
  const dl = () => download(`/identifiers/batches/${done.id}/excel`, {}, `${done.batch_no}.xlsx`).catch((e) => toast.error(apiError(e)));
  return (
    <div className="rounded bg-emerald-50 p-2 text-emerald-800 space-y-1" data-testid="impbc-done">
      <div><b>{done.label}</b> · {done.batch_no} · {done.count} kod{done.reused_count ? ` (${done.reused_count} mevcut çift yeniden kullanıldı)` : ""}</div>
      <div className="max-h-28 overflow-y-auto font-mono text-xs">{done.pairs.map((p) => <div key={p.code} data-testid={`impbc-pair-${p.code}`}>{p.code} → {p.gtin} {p.product_name && <span className="font-sans">· {p.product_name}</span>}</div>)}</div>
      <div className="flex gap-2"><Button size="sm" variant="outline" onClick={dl} data-testid="impbc-excel"><Download className="h-4 w-4 mr-1" />Excel</Button>
        <Button size="sm" variant="outline" onClick={() => setLbl(true)} data-testid="impbc-labels"><Tag className="h-4 w-4 mr-1" />Etiket Bas</Button></div>
      {lbl && <LabelDialog items={done.pairs.map((p) => ({ code: p.code, sku: p.code, name: `${p.gtin}${p.product_name ? ` · ${p.product_name}` : ""}`, copies: 1 }))}
        endpoint={`/identifiers/batches/${done.id}/labels`} title={`Etiket Bas · ${done.label}`} onClose={() => setLbl(false)} />}
    </div>
  );
}

export default function ImportBarcodeGen({ poId }) {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [idem, setIdem] = useState(() => crypto.randomUUID());
  const load = (s = q) => api.get("/identifiers/import-barcodes", { params: { q: s, po_id: poId || "" } }).then(({ data: d }) => setData(d)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(""); }, [poId]); // eslint-disable-line react-hooks/exhaustive-deps
  const lines = useMemo(() => (data?.orders || []).flatMap((o) => o.lines), [data]);
  const elig = lines.filter((l) => l.eligible);
  const chosen = elig.filter((l) => sel[key(l)]);
  const codes = new Set(chosen.map((l) => l.sku.trim().toUpperCase()));
  const toggle = (l) => setSel({ ...sel, [key(l)]: !sel[key(l)] });
  const all = elig.length > 0 && chosen.length === elig.length;
  const run = async () => {
    setBusy(true);
    try {
      const { data: b } = await api.post("/identifiers/import-barcodes", { lines: chosen.map((l) => ({ po_id: l.po_id, line_id: l.line_id })) }, { headers: { "Idempotency-Key": idem } });
      setDone(b); setSel({}); setIdem(crypto.randomUUID()); toast.success(`${b.label}: ${b.count} ürün koduna barkod verildi`); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (!data) return <div className="h-20 animate-pulse" />;
  return (
    <div className="space-y-3 text-sm" data-testid="impbc-panel">
      {data.gs1.blocked && <div className="rounded border border-rose-300 bg-rose-50 p-2 text-rose-700" data-testid="impbc-blocked">{data.gs1.blocked}</div>}
      <div className="flex items-center gap-2">
        <div className="relative flex-1"><Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="pl-8" placeholder="Sipariş no, tedarikçi, ürün adı veya kodu" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="impbc-search" /></div>
        <Button size="sm" variant="outline" onClick={() => load()} data-testid="impbc-search-btn">Ara</Button>
        <span className="text-xs text-slate-500">Boş GTIN: <b className="font-mono" data-testid="impbc-avail-gtin">{formatNumber(data.gs1.available_gtin || 0)}</b></span>
      </div>
      <div className="max-h-[48vh] overflow-y-auto rounded border">
        <table className="erp-table text-[13px]"><thead><tr><th><input type="checkbox" checked={all} disabled={!elig.length} onChange={() => setSel(all ? {} : Object.fromEntries(elig.map((l) => [key(l), true])))} data-testid="impbc-select-all" /></th>
          <th>Ürün Kodu</th><th>Ürün</th><th>Kart</th><th className="text-right">Miktar</th><th>Barkod</th></tr></thead>
          <tbody>{data.orders.length === 0 ? <tr><td colSpan={6} className="py-6 text-center text-slate-400" data-testid="impbc-empty">Barkodsuz ürün kodu olan uygun sipariş yok</td></tr> : data.orders.map((o) => [
            <tr key={o.id} className="bg-slate-100" data-testid={`impbc-order-${o.po_no}`}><td colSpan={6} className="text-xs font-semibold">{o.po_no} · {o.supplier_name} <span className="font-normal text-slate-500">· {o.missing} barkodsuz satır</span></td></tr>,
            ...o.lines.map((l, i) => <Line key={`${o.id}-${l.line_id || i}`} l={l} on={sel[key(l)]} toggle={toggle} />)])}</tbody></table>
      </div>
      {done && <Result done={done} />}
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-600" data-testid="impbc-selected">{chosen.length} satır · {codes.size} benzersiz ürün kodu (aynı kod tek barkod alır)</span>
        <Button disabled={!chosen.length || busy || !!data.gs1.blocked} onClick={run} data-testid="impbc-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Barkod Üret</Button>
      </div>
    </div>
  );
}
